import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowSquareOut, CheckCircle, XCircle } from "@phosphor-icons/react";
import api from "../../lib/api";
import { fmtEUR } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const STATES = ["", "awaiting_payment", "awaiting_confirmation", "credited", "rejected", "cancelled"];

const BADGE = {
  awaiting_payment: "bg-amber-50 text-amber-700",
  awaiting_confirmation: "bg-cobalt-50 text-cobalt-600",
  credited: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-600",
  cancelled: "bg-ink-100 text-ink-500",
};

export default function AdminCrypto() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMaster = user?.is_master_admin || user?.role === "admin";
  const [deposits, setDeposits] = useState([]);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () =>
    api.get(`/admin/crypto-deposits${filter ? `?state=${filter}` : ""}`)
      .then(({ data }) => setDeposits(data)).catch(() => {});

  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async (d) => {
    let tx = d.tx_hash;
    if (!tx) {
      tx = window.prompt("Enter the on-chain transaction hash to credit this deposit:");
      if (!tx) return;
    }
    if (!window.confirm(`Credit ${fmtEUR(d.amount_eur)} to ${d.email}?\n\nOnly confirm after you have verified the transaction on the blockchain explorer.`)) return;
    setBusy(d.id);
    try {
      const { data } = await api.post(`/admin/crypto-deposits/${d.id}/confirm`, { tx_hash: tx });
      toast.success(`Credited ${fmtEUR(data.credited_eur || d.amount_eur)} — new balance ${fmtEUR(data.balance_eur ?? 0)}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Confirm failed");
    } finally {
      setBusy(null);
    }
  };

  const reject = async (d) => {
    const reason = window.prompt("Rejection reason (visible internally):");
    if (!reason) return;
    setBusy(d.id);
    try {
      await api.post(`/admin/crypto-deposits/${d.id}/reject`, { reason });
      toast.success("Deposit rejected");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="admin-crypto-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">{t("admin.crypto", "Crypto Deposits")}</h1>
          <p className="text-sm text-ink-500 mt-1">Verify deposits on-chain before crediting. A transaction hash can only ever be credited once.</p>
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
          className="input-soft text-sm !py-2 !w-auto" data-testid="admin-crypto-filter">
          {STATES.map((s) => <option key={s} value={s}>{s ? s.replace(/_/g, " ") : "All states"}</option>)}
        </select>
      </div>

      <div className="card-soft overflow-x-auto !rounded-2xl">
        <table className="w-full text-sm" data-testid="admin-crypto-table">
          <thead className="bg-cobalt-50 text-xs uppercase tracking-widest text-cobalt-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Reference</th>
              <th className="text-left px-4 py-3 font-semibold">Customer</th>
              <th className="text-left px-4 py-3 font-semibold">Asset · Network</th>
              <th className="text-right px-4 py-3 font-semibold">EUR</th>
              <th className="text-right px-4 py-3 font-semibold">Expected</th>
              <th className="text-left px-4 py-3 font-semibold">Tx hash</th>
              <th className="text-left px-4 py-3 font-semibold">State</th>
              <th className="text-left px-4 py-3 font-semibold">Created</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {deposits.map((d) => (
              <tr key={d.id} className="border-t border-ink-100 hover:bg-sky-soft/40" data-testid={`admin-crypto-row-${d.reference}`}>
                <td className="px-4 py-3 font-mono text-xs">{d.reference}</td>
                <td className="px-4 py-3">{d.email}</td>
                <td className="px-4 py-3 font-semibold">{d.symbol} <span className="text-ink-500 font-normal">· {d.network}</span></td>
                <td className="px-4 py-3 text-right font-display font-bold">{fmtEUR(d.amount_eur)}</td>
                <td className="px-4 py-3 text-right text-xs">{d.expected_crypto_amount} {d.symbol}</td>
                <td className="px-4 py-3 text-xs max-w-[140px]">
                  {d.tx_hash ? (
                    d.explorer_url ? (
                      <a href={d.explorer_url} target="_blank" rel="noreferrer"
                        className="text-cobalt-600 hover:underline inline-flex items-center gap-1"
                        data-testid={`admin-crypto-explorer-${d.reference}`}>
                        <span className="truncate max-w-[100px] inline-block align-middle">{d.tx_hash}</span>
                        <ArrowSquareOut size={12} />
                      </a>
                    ) : <span className="truncate max-w-[120px] inline-block">{d.tx_hash}</span>
                  ) : <span className="text-ink-400">—</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${BADGE[d.state] || "bg-ink-100 text-ink-500"}`}>
                    {d.state.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-ink-500">{(d.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {["awaiting_payment", "awaiting_confirmation"].includes(d.state) && isMaster && (
                    <>
                      <button onClick={() => confirm(d)} disabled={busy === d.id}
                        className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg px-2.5 py-1.5 text-xs font-semibold mr-2 disabled:opacity-50"
                        data-testid={`admin-crypto-confirm-${d.reference}`}>
                        <CheckCircle size={14} weight="fill" /> Credit
                      </button>
                      <button onClick={() => reject(d)} disabled={busy === d.id}
                        className="inline-flex items-center gap-1 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg px-2.5 py-1.5 text-xs font-semibold disabled:opacity-50"
                        data-testid={`admin-crypto-reject-${d.reference}`}>
                        <XCircle size={14} weight="fill" /> Reject
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {deposits.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-ink-500" data-testid="admin-crypto-empty">No crypto deposits yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
